import { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from '../../button/Button';
import { useQuery } from '../../request/hooks/useQuery';
import { PreferencesFactory } from './Factory';
import { SeverityGroup } from './components/SeverityGroup';
import { SEVERITY_TYPES } from './constant';
import styles from './Preferences.module.scss';
import { useInMemoryFilteredList } from '../../shared/hooks/useInMemoryFilteredList';
import { EntityUIState } from '../../request/interfaces/entity-ui-state';
import { GenericPageShimmer } from '../../shimmer/GenericPageShimmer/GenericPageShimmer';
import { RetryApiAlert } from '../../shared/components/RetryApiAlert/RetryApiAlert';
import { getErrorMessageFromAxiosError } from '../../request/utils';
import { ListMetaNetworkParams } from '../../list-conductor/list-meta-data';
import { toasterService } from '../../toaster/toaster.service';
import { ChannelMeta, Preference, SeverityType } from './Interface';
import { AlertsAPIservice } from '../APIServices/APIServiceContext';

export function Preferences() {
  const [alerts, setAlerts] = useState<Preference[]>([]);
  const changeCaptureRef = useRef(false);
  const [channelsMeta, setChannelsMeta] = useState<ChannelMeta[]>([]);
  const { APIService, activeRecipientID } = useContext(AlertsAPIservice);

  const { fetch: fetchPreferences, reset } = useInMemoryFilteredList(
    async () =>
      Promise.all([APIService.getPreferences(), APIService.getUserPreferencesMetaData()]).then(
        ([preferences, metaData]) => {
          setChannelsMeta(
            metaData.data.channel_details.filter(channel => channel.value !== 'slack')
          );

          const preferencesFactoryData = PreferencesFactory(preferences.data.preferences);

          setAlerts(preferencesFactoryData);
          return preferencesFactoryData;
        }
      ),
    () => {
      return true;
    }
  );

  const { fetch, data, uiState, writeData, error } = useQuery({
    paginated: true,
    dataSource$: async params => {
      return fetchPreferences(params as ListMetaNetworkParams);
    },
    listConductorOptions: {
      handleQueryParams: false,
      paginationStrategy: undefined,
      defaultListMeta: { filters: [], sort: {} }
    }
  });

  useEffect(() => {
    reset();
    fetch();
  }, [activeRecipientID]);

  useEffect(() => {
    if (alerts.length) {
      writeData((previousData: Preference[] = []) => {
        return previousData?.map(_data => {
          const alert = alerts.find(_alert => _alert.type === _data.type) || _data;
          return {
            ...alert
          };
        });
      });
    }
  }, [alerts]);

  const getSeverityAlerts = (severity: SeverityType) => {
    return data?.filter(alert => alert.severity === severity);
  };

  const onChannelPreferenceForSeverityChange = (
    alertsSet: Set<string>,
    channelType: string,
    selected: boolean
  ) => {
    setAlerts(prev =>
      prev.map((_alert: Preference) => {
        const alert = _alert;
        if (alertsSet.has(_alert.type)) {
          alert.configs[channelType as keyof typeof alert.configs] = selected;
        }
        return alert;
      })
    );

    changeCaptureRef.current = true;
  };

  const onChannelPreferenceForAlertChange = (
    alertType: string,
    channelType: string,
    selected: boolean
  ) => {
    setAlerts(prev =>
      prev.map(_alert => {
        const alert = _alert;
        if (_alert.type === alertType) {
          alert.configs[channelType as keyof typeof alert.configs] = selected;
        }
        return alert;
      })
    );
    changeCaptureRef.current = true;
  };

  const savePreferences = async () => {
    await APIService.savePreferences(alerts);
    toasterService.pop('success', 'Preferences saved successfully');
  };

  return (
    <div className='h-100 overflow-auto'>
      <div className='body-container'>
        {uiState === EntityUIState.IDLE && channelsMeta && data ? (
          <>
            <div
              className={clsx(
                'd-flex w-100 justify-between align-items-center pb-3',
                styles.preferencesHeader
              )}
            >
              <span className='text-subheading-2'>Alert Preferences</span>

              {changeCaptureRef.current ? (
                <Button onClick={savePreferences} icon='save' dataId='save-preferences'>
                  Save Changes
                </Button>
              ) : null}
            </div>

            {SEVERITY_TYPES.map((severity, index) => {
              const severityGroupedAlerts = getSeverityAlerts(severity);
              return severityGroupedAlerts?.length ? (
                <SeverityGroup
                  className={clsx(
                    index < SEVERITY_TYPES.length - 1 ? 'mb-3' : '',
                    styles.severityGroupedAlertsBox
                  )}
                  key={severity}
                  onChannelPreferenceForAlertChange={onChannelPreferenceForAlertChange}
                  onChannelPreferenceForSeverityChange={onChannelPreferenceForSeverityChange}
                  severity={severity}
                  alerts={severityGroupedAlerts}
                  channelsMeta={channelsMeta}
                />
              ) : null;
            })}
          </>
        ) : null}
      </div>

      {uiState === EntityUIState.ERRORED ? (
        <div className='h-100 center-flex-row align-items-center'>
          <RetryApiAlert
            dataId='preferences'
            secondaryCTAProps={{
              onClick: () => fetch()
            }}
            subTitle={error ? getErrorMessageFromAxiosError(error) : undefined}
          />
        </div>
      ) : null}

      {uiState === EntityUIState.LOADING ? <GenericPageShimmer dataId='preferences' /> : null}
    </div>
  );
}
